import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  PersonOutlined,
  PaletteOutlined,
  LanguageOutlined,
  NotificationsOutlined,
  LockOutlined,
  PhotoCamera,
  Delete,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { isTokenExpired } from 'hooks/useAuthCheck';
import {
  setLanguage,
  setMode,
  setTimeMode,
  setOpenSidebar,
  setLogout,
} from 'state';
import { BACKEND_URL, LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { updatePreferences } from 'state/api';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import ChangePassword from './ChangePassword';
import BiometricSetup from './BiometricSetup';


const profileSchema = yup.object({
  firstName: yup.string().required('Required'),
  lastName: yup.string().required('Required'),
  email: yup.string().email('Invalid email').required('Required'),
});

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'vi', label: 'Tiếng Việt' },
];

const SettingRow = ({ label, description, children }) => (
  <Box
    display="grid"
    gridTemplateColumns={{ xs: '1fr auto', md: '1fr 1fr' }}
    alignItems="center"
    gap={2}
    py={2}
  >
    <Box>
      <Typography variant="body1">{label}</Typography>
      {description && (
        <Typography variant="caption" color="text.secondary" display="block">
          {description}
        </Typography>
      )}
    </Box>
    <Box>{children}</Box>
  </Box>
);


const Settings = () => {
  useAuthCheck();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;

  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const scrollRef = useRef(null);
  const imageInputRef = useRef(null);

  const user = useSelector((s) => s.user);
  const token = useSelector((s) => s.token);
  const mode = useSelector((s) => s.mode);
  const timemode = useSelector((s) => s.timemode);
  const language = useSelector((s) => s.language);
  const isSidebarOpen = useSelector((s) => s.isSidebarOpen);

  const userId = user?._id;

  const [tab, setTab] = useState(0);

  const [notificationEnabled, setNotificationEnabled] = useState(
    user?.preferences?.notificationEnabled ?? true
  );

  // ── Profile tab state ──
  const [image, setImage] = useState(null);
  const [picture, setPicture] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [profileValues, setProfileValues] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    location: user?.location || '',
    occupation: user?.occupation || '',
    email: user?.email || '',
  });

  useEffect(() => {
    const picturePath = user?.picturePath;
    setImage(picturePath && picturePath !== 'undefined' ? `${BACKEND_URL}/assets/${picturePath}` : null);
    setProfileValues({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      location: user?.location || '',
      occupation: user?.occupation || '',
      email: user?.email || '',
    });
  }, [user]);

  useEffect(() => {
    return () => {
      if (image && typeof image === 'string' && image.startsWith('blob:')) {
        URL.revokeObjectURL(image);
      }
    };
  }, [image]);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) { setImage(URL.createObjectURL(file)); setPicture(file); }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) { setImage(URL.createObjectURL(file)); setPicture(file); }
  };

  const handleRemoveImage = () => {
    setImage(null); setPicture(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleProfileSubmit = useCallback(async (values, { resetForm }) => {
    try {
      const formData = new FormData();
      for (const key in values) formData.append(key, values[key]);
      if (picture) {
        formData.append('picture', picture);
        formData.append('picturePath', picture.name);
      }
      await api.put(`/auth/update/${userId}`, formData);
      toast.success('Profile updated successfully.');
      setPicture(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile!');
    }
  }, [userId, picture]);

  // ── Session / preferences ────────────────────────────────────────────────────
  const ensureSession = useCallback(() => {
    if (!token || isTokenExpired(token)) {
      toast.error('Session expired. Please log in again.');
      dispatch(setLogout());
      navigate('/auth/login');
      return false;
    }
    return true;
  }, [token, dispatch, navigate]);

  const savePrefs = useCallback(async (patch) => {
    if (!ensureSession()) return;
    try {
      await updatePreferences(userId, patch);
    } catch (err) {
      if ([401, 403].includes(err.response?.status)) {
        toast.error('Session expired. Please log in again.');
        dispatch(setLogout());
      } else {
        toast.error('Failed to save preference!');
      }
    }
  }, [userId, ensureSession, dispatch]);

  const handleToggleTheme = useCallback(async () => {
    dispatch(setMode());
    await savePrefs({ themeMode: mode === 'dark' ? 'light' : 'dark' });
  }, [dispatch, mode, savePrefs]);

  const handleToggleSidebar = useCallback(async () => {
    dispatch(setOpenSidebar());
    await savePrefs({ isSidebarOpen: !isSidebarOpen });
  }, [dispatch, isSidebarOpen, savePrefs]);

  const handleLanguageChange = useCallback(async (e) => {
    const val = e.target.value;
    dispatch(setLanguage(val));
    await savePrefs({ language: val });
  }, [dispatch, savePrefs]);

  const handleToggleTimeMode = useCallback(async () => {
    dispatch(setTimeMode());
    await savePrefs({ timeMode: timemode === 'UTC' ? 'LocalBase' : 'UTC' });
  }, [dispatch, timemode, savePrefs]);

  const handleToggleNotification = useCallback(async (e) => {
    const val = e.target.checked;
    setNotificationEnabled(val);
    await savePrefs({ notificationEnabled: val });
  }, [savePrefs]);

  const sectionPadding = { px: 0, py: 1 };

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Settings" 
          subtitle="Personalization" 
        />
      </FlexBetween>

      <Box 
        display="grid" 
        gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
        gap={2}
      >
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}
          sx={{ gridColumn: 'span 12', overflowY: 'auto' }}
        >
          <Box
            sx={{
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
            }}
          >
            <Tabs
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.alt,
                minHeight: 40,
                '& .MuiTab-root': { minHeight: 40, py: 0.5 },
              }}
            >
              <Tab label="Profile"       icon={<PersonOutlined />}          iconPosition="start" />
              <Tab label="Appearance"    icon={<PaletteOutlined />}         iconPosition="start" />
              <Tab label="Localization"  icon={<LanguageOutlined />}        iconPosition="start" />
              <Tab label="Notifications" icon={<NotificationsOutlined />}   iconPosition="start" />
              <Tab label="Security"      icon={<LockOutlined />}            iconPosition="start" />
            </Tabs>

            <Box p={3}>
              {/* ── Tab 0: Profile ── */}
              {tab === 0 && (
                <Box {...sectionPadding}>
                  <Formik
                    initialValues={profileValues}
                    enableReinitialize
                    validationSchema={profileSchema}
                    onSubmit={handleProfileSubmit}
                  >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                      <form onSubmit={handleSubmit}>
                        {/* Avatar */}
                        <Box display="flex" alignItems="center" gap={2} mb={3}>
                          <Box sx={{ position: 'relative', flexShrink: 0 }}>
                            <Avatar
                              src={image || undefined}
                              sx={{
                                width: 100,
                                height: 100,
                                border: `1px solid ${theme.palette.divider}`,
                                fontSize: '2rem',
                              }}
                            />
                            {image && (
                              <IconButton
                                size="small"
                                onClick={handleRemoveImage}
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  p: 0.5,
                                  color: '#fff',
                                  bgcolor: 'error.main',
                                  '&:hover': { bgcolor: 'error.dark' },
                                }}
                              >
                                <Delete sx={{ fontSize: 12 }} />
                              </IconButton>
                            )}
                          </Box>

                          <Box
                            onClick={() => imageInputRef.current.click()}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            sx={{
                              flex: 1,
                              height: 100,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                              border: `2px dashed ${isDragOver ? primaryMain : theme.palette.divider}`,
                              borderRadius: 2,
                              bgcolor: isDragOver ? `${primaryMain}12` : 'transparent',
                              cursor: 'pointer',
                              transition: 'border-color 0.2s, background-color 0.2s',
                              '&:hover': { borderColor: primaryMain, bgcolor: `${primaryMain}08` },
                            }}
                          >
                            <PhotoCamera sx={{ color: isDragOver ? primaryMain : 'text.secondary', fontSize: 24 }} />
                            <Typography variant="body2" color={isDragOver ? 'primary' : 'text.secondary'}>
                              {isDragOver ? 'Drop to upload' : 'Drag & drop or click to upload'}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              PNG, JPG, WEBP
                            </Typography>
                          </Box>

                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            ref={imageInputRef}
                            onChange={handleImageChange}
                            style={{ display: 'none' }}
                          />
                        </Box>

                        {/* Fields */}
                        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                          <TextField
                            size="small"
                            label="First Name"
                            name="firstName"
                            value={values.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.firstName && Boolean(errors.firstName)}
                            helperText={touched.firstName && errors.firstName}
                          />
                          <TextField
                            size="small"
                            label="Last Name"
                            name="lastName"
                            value={values.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.lastName && Boolean(errors.lastName)}
                            helperText={touched.lastName && errors.lastName}
                          />
                          <TextField
                            size="small"
                            label="Location"
                            name="location"
                            value={values.location}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <TextField
                            size="small"
                            label="Occupation"
                            name="occupation"
                            value={values.occupation}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <TextField
                            size="small"
                            label="Email"
                            name="email"
                            type="email"
                            value={values.email}
                            disabled
                            sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}
                          />
                        </Box>

                        <Box mt={2}>
                          <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
                          >
                            {isSubmitting ? 'Saving…' : 'Save'}
                          </Button>
                        </Box>
                      </form>
                    )}
                  </Formik>
                </Box>
              )}

              {/* ── Tab 1: Appearance ── */}
              {tab === 1 && (
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                  gap={0}
                  {...sectionPadding}
                >
                  <SettingRow
                    label="Theme"
                    description={`Current: ${mode === 'dark' ? 'Dark' : 'Light'} mode`}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={mode === 'dark'}
                          onChange={handleToggleTheme}
                          color="primary"
                        />
                      }
                      label={mode === 'dark' ? 'Dark' : 'Light'}
                      labelPlacement="start"
                      sx={{ mr: 0 }}
                    />
                  </SettingRow>

                  <SettingRow
                    label="Sidebar open by default"
                    description={`Current: ${isSidebarOpen ? 'Open' : 'Close'}`}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isSidebarOpen}
                          onChange={handleToggleSidebar}
                          color="primary"
                        />
                      }
                      label={isSidebarOpen ? 'Open' : 'Close'}
                      labelPlacement="start"
                      sx={{ mr: 0 }}
                    />
                  </SettingRow>
                </Box>
              )}

              {/* ── Tab 2: Localization ── */}
              {tab === 2 && (
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                  gap={0}
                  {...sectionPadding}
                >
                  <SettingRow
                    label="Language"
                    description="Display language"
                  >
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Language</InputLabel>
                      <Select
                        value={language}
                        label="Language"
                        onChange={handleLanguageChange}
                      >
                        {LANGUAGES.map((l) => (
                          <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </SettingRow>

                  <SettingRow
                    label="Time mode"
                    description={`Current: ${timemode === 'UTC' ? 'UTC' : 'Local Base'}`}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={timemode !== 'UTC'}
                          onChange={handleToggleTimeMode}
                          color="primary"
                        />
                      }
                      label={timemode === 'UTC' ? 'UTC' : 'Local Base'}
                      labelPlacement="start"
                      sx={{ mr: 0 }}
                    />
                  </SettingRow>
                </Box>
              )}

              {/* ── Tab 3: Notifications ── */}
              {tab === 3 && (
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                  gap={0}
                  {...sectionPadding}
                >
                  {!('Notification' in window) && (
                    <Alert severity="info" sx={{ mb: 2, gridColumn: '1 / -1' }}>
                      Your browser does not support push notifications.
                    </Alert>
                  )}
                  <SettingRow
                    label="Push notifications"
                    description="Receive real-time events"
                  >
                    <Switch
                      checked={notificationEnabled}
                      onChange={handleToggleNotification}
                      color="primary"
                      disabled={!('Notification' in window)}
                    />
                  </SettingRow>
                </Box>
              )}

              {/* ── Tab 4: Security ── */}
              {tab === 4 && (
                <Box {...sectionPadding}>
                  <Box
                    display="flex"
                    flexDirection={isDesktop ? 'row' : 'column'}
                    alignItems={isDesktop ? 'flex-start' : 'stretch'}
                    gap={4}
                  >
                    <Box flex={1} minWidth={0}>
                      <ChangePassword userId={userId} />
                    </Box>

                    <Box flex={1} minWidth={0}>
                      <BiometricSetup />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;
